import type { ActionSlug, ModuleSlug } from '@/lib/permissions';

/*
 * How the permission-set editor GROUPS and LABELS the catalogue — owner's
 * decisions 2026-10-02 (sections, view-only switches, one per-module scope
 * picker on the modules where the server enforces `own`).
 *
 * ⚠️ This module decides PRESENTATION only. Which (module, action) pairs exist
 * is the server's answer, read from `GET /permissions`; nothing here invents a
 * row. A module missing from `SECTION_OF` is never dropped — it lands in
 * `Other`, at the end, because the catalogue grows on the server without a
 * frontend deploy.
 */

export type SectionId =
  | 'sales'
  | 'enquiries'
  | 'marketing'
  | 'insights'
  | 'delivery'
  | 'work'
  | 'dashboards'
  | 'trackers'
  | 'catalogue'
  | 'finance'
  | 'settings'
  | 'admin'
  | 'other';

export const SECTIONS: readonly { id: SectionId; name: string }[] = [
  { id: 'sales', name: 'Sales' },
  { id: 'enquiries', name: 'Enquiries' },
  { id: 'marketing', name: 'B2B Marketing' },
  { id: 'insights', name: 'Product Insights' },
  { id: 'delivery', name: 'Delivery' },
  { id: 'work', name: 'Work' },
  { id: 'dashboards', name: 'Dashboards' },
  { id: 'trackers', name: 'Trackers' },
  { id: 'catalogue', name: 'Catalogue' },
  { id: 'finance', name: 'Finance' },
  { id: 'settings', name: 'Settings & integrations' },
  { id: 'admin', name: 'Admin & security' },
  { id: 'other', name: 'Other' },
];

const EXPLICIT: readonly [SectionId, readonly ModuleSlug[]][] = [
  ['sales', ['companies', 'contacts', 'deals', 'quotes', 'invoices', 'credit-notes', 'projects']],
  ['enquiries', ['event-interest', 'b2b-proposal', 'report-download']],
  // Explicit, never a `marketing-` prefix: `marketing-settings` must not be
  // read as a member of the `settings-` family by a future prefix reorder.
  [
    'marketing',
    [
      'marketing-campaigns',
      'marketing-lists',
      'marketing-templates',
      'marketing-email-accounts',
      'marketing-settings',
    ],
  ],
  ['insights', ['product-insights']],
  ['delivery', ['deliverables']],
  ['work', ['tasks', 'notes', 'targets', 'sales-tracker']],
  ['catalogue', ['products', 'cycles', 'intakes']],
  ['dashboards', ['sales-targets']],
  ['finance', ['commissions']],
  ['settings', ['settings', 'integrations']],
  ['admin', ['users', 'permission-sets', 'api-keys', 'support-sessions', 'api-logs']],
];

const SECTION_OF: ReadonlyMap<string, SectionId> = new Map(
  EXPLICIT.flatMap(([section, slugs]) => slugs.map((slug) => [slug, section] as const)),
);

/** Prefix families — new `dashboards-*`, `trackers-*`, … land in place. */
const PREFIXES: readonly [string, SectionId][] = [
  ['dashboards-', 'dashboards'],
  ['trackers-', 'trackers'],
  ['reference-', 'catalogue'],
  ['settings-', 'settings'],
];

export function sectionOf(moduleSlug: string): SectionId {
  const explicit = SECTION_OF.get(moduleSlug);
  if (explicit) return explicit;
  for (const [prefix, section] of PREFIXES) {
    if (moduleSlug.startsWith(prefix)) return section;
  }
  return 'other';
}

/**
 * Modules whose only meaningful permission is `read` — rendered as ONE
 * "Can see" switch. The switch writes every catalogue row of the module, so a
 * leftover inert action can never stay granted under a switch that reads off.
 */
export const VIEW_ONLY_MODULES: ReadonlySet<string> = new Set<ModuleSlug>([
  'dashboards-university-partnerships',
  'dashboards-up-lead-switcher',
  'dashboards-management',
  'dashboards-operations',
  'trackers-my-tasks',
  'trackers-client-deliverables',
]);

/**
 * Modules where the server really filters by owner, so `own` narrows.
 * Only these show the "Records: All / Only theirs" picker; everywhere else a
 * grant is written at `all`.
 */
export const OWN_ENFORCED_MODULES: ReadonlySet<string> = new Set<ModuleSlug>([
  'companies',
  'contacts',
  'deals',
  'quotes',
  'invoices',
  'deliverables',
  'projects',
  'credit-notes',
  'commissions',
  'trackers-invoice',
  'tasks',
  'notes',
  'targets',
  'sales-tracker',
]);

const ACTION_LABELS: Readonly<Partial<Record<ActionSlug, string>>> & Readonly<Record<string, string | undefined>> = {
  read: 'View',
  create: 'Create',
  update: 'Edit',
  delete: 'Delete',
  convert: 'Convert',
  approve: 'Approve',
  send: 'Send',
  export: 'Export',
  triage: 'Triage',
  'manage-access': 'Manage access',
};

/** An action slug the map does not know falls back to the server's own name. */
export function actionLabel(actionSlug: string, serverName: string): string {
  return ACTION_LABELS[actionSlug] ?? serverName;
}
